<script setup>
import { computed, onMounted, onUnmounted, ref } from 'vue';
import { useStore, useMapGetter } from 'dashboard/composables/store';
import { useI18n } from 'vue-i18n';
import { useAccount } from 'dashboard/composables/useAccount';
import { isNotReadyStatus } from 'dashboard/helper/phoneHelper';
import {
  getStatusPriority,
  deriveCrossQueueStatus,
} from './helpers/wallboardHelper';

const store = useStore();
const { t, te } = useI18n();
const { accountId } = useAccount();

const phoneQueues = useMapGetter('phoneQueues/getPhoneQueues');
const uiFlags = useMapGetter('phoneQueues/getUIFlags');
const unhandledMissedCount = useMapGetter(
  'phoneQueues/getUnhandledMissedCount'
);
const wallboardStats = useMapGetter('phoneQueues/getWallboardStats');
const allAgentsInAccount = useMapGetter('agents/getAgents');
const currentAccount = computed(() =>
  store.getters['accounts/getAccount'](accountId.value)
);
const customAgentPhoneStatuses = computed(
  () => currentAccount.value?.agent_phone_statuses || []
);

const currentTime = ref(Date.now());
let intervalId = null;

onMounted(async () => {
  await store.dispatch('agents/get');
  await store.dispatch('phoneQueues/get');
  await store.dispatch('phoneQueues/getWallboardStats');

  intervalId = setInterval(() => {
    currentTime.value = Date.now();
  }, 1000);
});

onUnmounted(() => {
  if (intervalId) {
    clearInterval(intervalId);
  }
});

const formatDuration = seconds => {
  if (seconds < 0) return '0s';

  const hours = Math.floor(seconds / 3600);
  const minutes = Math.floor((seconds % 3600) / 60);
  const secs = seconds % 60;

  const parts = [];
  if (hours > 0) parts.push(`${hours}h`);
  if (minutes > 0) parts.push(`${minutes}m`);
  if (secs > 0 || parts.length === 0) parts.push(`${secs}s`);

  return parts.join(' ');
};

const calculateDuration = statusChangedAt => {
  if (!statusChangedAt) return '0s';

  const now = currentTime.value;
  const changedAt = new Date(statusChangedAt).getTime();
  const seconds = Math.floor((now - changedAt) / 1000);

  return formatDuration(seconds);
};

// Most recent per-queue status change = when the agent entered their current
// (possibly mixed) configuration. Falls back to the merged value for agents
// with no per-queue entries (e.g. chat-only).
const effectiveStatusChangedAt = agent => {
  const times = Array.from(agent.queueStatuses?.values() || [])
    .map(q => q.statusChangedAt)
    .filter(Boolean)
    .map(changedAt => new Date(changedAt).getTime());
  if (!times.length) return agent.status_changed_at;
  return new Date(Math.max(...times)).toISOString();
};

const queueGroups = computed(() => {
  const groups = new Map();
  phoneQueues.value.forEach(queue => {
    // Only include queues that have at least one agent
    if (queue.agents && queue.agents.length > 0) {
      const firstWord = queue.name.split(' ')[0];
      if (!groups.has(firstWord)) {
        groups.set(firstWord, []);
      }
      groups.get(firstWord).push(queue);
    }
  });
  return Array.from(groups.entries()).sort((a, b) => a[0].localeCompare(b[0]));
});

const mergeAgentRecordStatus = (merged, newStatus) => {
  if (!merged) return newStatus;
  if (merged === 'QUEUE_CALL') return merged;
  if (newStatus === 'QUEUE_CALL') return newStatus;
  return merged;
};

const showChatStatus = computed(() => {
  return currentAccount.value?.wallboard_chat_status_enabled || false;
});

const chatStatusToPhoneStatus = chatStatus => {
  if (chatStatus === 'online') return 'CHAT_ONLINE';
  if (chatStatus === 'busy') return 'CHAT_BUSY';
  if (chatStatus === 'offline') return 'CHAT_OFFLINE';
  return 'NO_ACCESS';
};

const allAgents = computed(() => {
  const agentsMap = new Map();

  allAgentsInAccount.value.forEach(agent => {
    const key = agent.id;
    agentsMap.set(key, {
      id: agent.id,
      name: agent.name,
      extension: null,
      status: null,
      status_changed_at: null,
      queues: [],
      queueStatuses: new Map(),
      availability_status: agent.availability_status,
      has_phone_credential: !!agent.phone_username,
      dnd: agent.dnd || false,
    });
  });

  phoneQueues.value.forEach(queue => {
    queue.agents.forEach(agent => {
      const key = agent.id;
      let agentRecord = agentsMap.get(key);

      if (!agentRecord) {
        agentRecord = {
          ...agent,
          queues: [],
          queueStatuses: new Map(),
          has_phone_credential: !!agent.extension,
        };
        agentsMap.set(key, agentRecord);
      } else {
        agentRecord.extension = agent.extension || agentRecord.extension;
        agentRecord.status = mergeAgentRecordStatus(
          agentRecord.status,
          agent.status
        );
        agentRecord.status_changed_at =
          agent.status_changed_at || agentRecord.status_changed_at;
        if (agent.extension) agentRecord.has_phone_credential = true;
      }

      agentRecord.queues.push(queue.name);
      agentRecord.queueStatuses.set(queue.id, {
        status: agent.status,
        queueName: queue.name,
        statusChangedAt: agent.status_changed_at,
      });
    });
  });

  let agents = Array.from(agentsMap.values());

  agents = agents.map(agent => {
    const queueStatuses = Array.from(agent.queueStatuses?.values() || []).map(
      q => q.status
    );
    const derived = deriveCrossQueueStatus(queueStatuses);
    if (derived) agent.status = derived;
    return agent;
  });

  if (showChatStatus.value) {
    agents = agents.map(agent => {
      if (!agent.status) {
        if (!agent.has_phone_credential && agent.availability_status) {
          agent.status = chatStatusToPhoneStatus(agent.availability_status);
        } else {
          agent.status = 'NO_ACCESS';
        }
      }

      return agent;
    });
  } else {
    agents = agents.filter(agent => agent.status);
  }

  return agents.sort((a, b) => {
    const priorityA = getStatusPriority(
      a.status,
      customAgentPhoneStatuses.value
    );
    const priorityB = getStatusPriority(
      b.status,
      customAgentPhoneStatuses.value
    );
    if (priorityA !== priorityB) {
      return priorityA - priorityB;
    }

    return (a.name || '').localeCompare(b.name || '');
  });
});

const agentDurations = computed(() => {
  const durations = {};
  const noTimerStatuses = [
    'READY',
    'OFFLINE',
    'ONLINE',
    'CHAT_ONLINE',
    'CHAT_BUSY',
    'CHAT_OFFLINE',
  ];
  allAgents.value.forEach(agent => {
    durations[agent.id] = noTimerStatuses.includes(agent.status)
      ? '0:00'
      : calculateDuration(effectiveStatusChangedAt(agent));
  });
  return durations;
});
const getAgentStatusInQueueGroup = (agent, queueGroup) => {
  const queues = queueGroup[1];
  const queueInGroup = queues.find(queue => agent.queueStatuses?.has(queue.id));
  if (queueInGroup) {
    return agent.queueStatuses.get(queueInGroup.id).status;
  }
  return 'no_access';
};

const getAgentChatStatusInPrefixGroup = (agent, prefix) => {
  const userAvailability =
    wallboardStats.value.userAvailabilityByInboxPrefix?.[prefix];
  if (!userAvailability || !agent.id) return 'no_access';
  return userAvailability[agent.id] || 'no_access';
};

const getStatusSquareClass = status => {
  if (!status) return '';
  if (status === 'no_access') return 'status-square-phone no-access';
  if (status === 'READY') return 'status-square-phone idle';
  if (status === 'OFFLINE') return 'status-square-phone offline';
  if (status === 'BREAK') return 'status-square-phone on-break';
  if (status === 'LUNCH') return 'status-square-phone on-lunch';
  if (status === 'AWAY') return 'status-square-phone on-away';
  if (status === 'DO_NOT_DISTURB') return 'status-square-phone on-dnd';
  if (status === 'BUSINESS_TRIP') return 'status-square-phone on-trip';

  if (['OTHER_CALL', 'QUEUE_CALL'].includes(status))
    return 'status-square-phone busy';

  if (customAgentPhoneStatuses.value.includes(status))
    return 'status-square-phone on-custom-break';

  return 'status-square-phone break';
};

const getChatStatusSquareClass = status => {
  if (!status) return '';
  if (status === 'online') return 'online';
  if (status === 'busy') return 'busy';
  if (status === 'offline') return 'offline';
  if (status === 'no_access') return 'no-access';
  return '';
};

const getChatStatusLabel = status => {
  if (!status) return '';
  if (status === 'no_access') {
    return t('WALLBOARD.CHAT_STATUS.NO_ACCESS');
  }
  return t(`PROFILE_SETTINGS.FORM.AVAILABILITY.STATUS.${status.toUpperCase()}`);
};

const getPhoneStatusLabel = status => {
  if (!status) return '';
  if (status === 'no_access') {
    return t('WALLBOARD.PHONE_STATUS.NO_ACCESS');
  }
  const key = `QUEUE_STATUS.${status}`;
  // eslint-disable-next-line @intlify/vue-i18n/no-dynamic-keys
  return te(key) ? t(key) : status;
};

const getReadyCount = computed(() => {
  return allAgents.value.filter(
    agent => agent.status === 'READY' || agent.status === 'CHAT_ONLINE'
  ).length;
});

const getOnCallCount = computed(() => {
  return allAgents.value.filter(agent =>
    ['QUEUE_CALL', 'OTHER_CALL'].includes(agent.status)
  ).length;
});

const getPausedCount = computed(() => {
  return allAgents.value.filter(
    agent =>
      agent.status === 'ONLINE' ||
      agent.status === 'CHAT_BUSY' ||
      isNotReadyStatus(agent.status, customAgentPhoneStatuses.value)
  ).length;
});

const getTotalWaitingCallers = computed(() => {
  return phoneQueues.value.reduce((total, queue) => {
    return total + (queue.callers?.length || 0);
  }, 0);
});

const getWaitingCallersForQueueGroup = queueGroup => {
  const queues = queueGroup[1];
  return queues.reduce((total, queue) => {
    return total + (queue.callers?.length || 0);
  }, 0);
};

const getMissedCallsForQueueGroup = queueGroup => {
  const queues = queueGroup[1];
  return queues.reduce((total, queue) => {
    return total + (queue.missed_calls_count || 0);
  }, 0);
};

const getHandledCallsForQueueGroup = queueGroup => {
  const queues = queueGroup[1];
  return queues.reduce((total, queue) => {
    return total + (queue.handled_calls_count || 0);
  }, 0);
};

const getOutboundCallsForQueueGroup = queueGroup => {
  const groupName = queueGroup[0].toLowerCase();
  return (
    wallboardStats.value.outboundCallsByOutboundRuleNamePrefix?.[groupName] || 0
  );
};

const getAvailabilityForQueueGroup = queueGroup => {
  const missedCalls = getMissedCallsForQueueGroup(queueGroup);
  const handledCalls = getHandledCallsForQueueGroup(queueGroup);

  if (handledCalls + missedCalls === 0) return 0;

  const pct = (handledCalls / (handledCalls + missedCalls)) * 100;

  return Math.round(pct);
};
</script>

<template>
  <div class="flex flex-col w-full h-full p-4">
    <h1 class="text-2xl font-semibold mb-4">
      {{ $t('SIDEBAR.WALLBOARD_OVERVIEW') }}
    </h1>

    <div v-if="uiFlags.isFetching" class="text-n-slate-11">
      {{ $t('WALLBOARD.LOADING') }}
    </div>

    <div class="grid grid-cols-5 gap-4 mb-6">
      <router-link
        :to="{ name: 'wallboard_queues' }"
        class="border rounded-lg p-4 block no-underline"
        :class="{
          'bg-n-solid-2 border-n-weak': getTotalWaitingCallers < 1,
          'bg-orange-500 border-orange-600 text-white':
            getTotalWaitingCallers >= 1 && getTotalWaitingCallers <= 5,
          'bg-red-600 border-red-700 text-white': getTotalWaitingCallers > 5,
        }"
      >
        <div class="text-sm">
          {{ $t('WALLBOARD.WAITING') }}
        </div>
        <div class="text-2xl font-semibold">{{ getTotalWaitingCallers }}</div>
      </router-link>
      <router-link
        :to="{ name: 'home', query: { phone_status: 'missed' } }"
        class="border rounded-lg p-4 block no-underline"
        :class="{
          'bg-n-solid-2 border-n-weak': unhandledMissedCount < 1,
          'bg-orange-500 border-orange-600 text-white':
            unhandledMissedCount >= 1 && unhandledMissedCount <= 10,
          'bg-red-600 border-red-700 text-white': unhandledMissedCount > 10,
        }"
      >
        <div class="text-sm">
          {{ $t('WALLBOARD.MISSED_CALLS') }}
        </div>
        <div class="text-2xl font-semibold">{{ unhandledMissedCount }}</div>
      </router-link>
      <div class="border border-n-weak rounded-lg p-4 bg-n-solid-2">
        <div class="text-sm text-n-slate-11">{{ $t('WALLBOARD.READY') }}</div>
        <div class="text-2xl font-semibold">{{ getReadyCount }}</div>
      </div>
      <div class="border border-n-weak rounded-lg p-4 bg-n-solid-2">
        <div class="text-sm text-n-slate-11">
          {{ $t('WALLBOARD.ON_CALL') }}
        </div>
        <div class="text-2xl font-semibold">{{ getOnCallCount }}</div>
      </div>
      <div class="border border-n-weak rounded-lg p-4 bg-n-solid-2">
        <div class="text-sm text-n-slate-11">
          {{ $t('WALLBOARD.PAUSED') }}
        </div>
        <div class="text-2xl font-semibold">{{ getPausedCount }}</div>
      </div>
    </div>

    <div class="table-wrapper">
      <div class="table-container">
        <table class="w-auto">
          <thead class="border-b border-n-weak text-n-slate-11">
            <tr class="text-n-slate-11 divide-x divide-n-weak">
              <th class="text-left font-medium w-20">
                {{ $t('WALLBOARD.STATUS') }}
              </th>
              <th class="text-right font-medium w-28">
                {{ $t('WALLBOARD.TIME') }}
              </th>
              <th class="text-left font-medium">
                {{ $t('WALLBOARD.AGENT') }}
              </th>
              <th class="text-left font-medium w-20">
                {{ $t('WALLBOARD.EXTENSION') }}
              </th>
              <th
                v-for="[groupName] in queueGroups"
                :key="groupName"
                class="text-center font-medium w-20"
              >
                {{ groupName }}
              </th>
            </tr>
          </thead>
          <tbody class="divide-y divide-n-weak text-n-slate-11">
            <tr
              v-for="agent in allAgents"
              :key="agent.id"
              class="divide-x divide-n-weak"
            >
              <td
                class="text-left font-medium"
                :class="{
                  'status-ready': agent.status === 'READY',
                  'status-partially-ready': agent.status === 'PARTIALLY_READY',
                  'status-mixed': agent.status === 'MIXED',
                  'status-break': agent.status === 'BREAK',
                  'status-lunch': agent.status === 'LUNCH',
                  'status-away': agent.status === 'AWAY',
                  'status-dnd': agent.status === 'DO_NOT_DISTURB',
                  'status-trip': agent.status === 'BUSINESS_TRIP',
                  'status-custom-break': customAgentPhoneStatuses.includes(
                    agent.status
                  ),
                  'status-not-ready': ['NOT_READY', 'WRAP_UP'].includes(
                    agent.status
                  ),
                  'status-on-call': ['OTHER_CALL', 'QUEUE_CALL'].includes(
                    agent.status
                  ),
                  'status-offline': ['OFFLINE', 'ONLINE'].includes(
                    agent.status
                  ),
                  'status-no-access': agent.status === 'NO_ACCESS',
                  'status-chat-online': agent.status === 'CHAT_ONLINE',
                  'status-chat-busy': agent.status === 'CHAT_BUSY',
                  'status-chat-offline': agent.status === 'CHAT_OFFLINE',
                }"
              >
                <div class="queue-status-bubble">
                  {{ getPhoneStatusLabel(agent.status) }}
                </div>
              </td>
              <td class="text-right font-medium whitespace-nowrap">
                {{ agentDurations[agent.id] }}
              </td>
              <td class="text-left font-medium text-nowrap">
                <div class="flex items-center justify-between gap-2">
                  <span>{{ agent.name }}</span>
                  <span
                    v-if="agent.dnd"
                    v-tooltip.top="$t('QUEUE_STATUS.DO_NOT_DISTURB')"
                    class="dnd-badge"
                  >
                    {{ $t('PHONE_CTRLS.STATUS.DND') }}
                  </span>
                </div>
              </td>
              <td class="text-left font-medium text-nowrap">
                {{ agent.extension || '—' }}
              </td>
              <td
                v-for="queueGroup in queueGroups"
                :key="queueGroup[0]"
                class="text-center"
              >
                <div class="flex items-center justify-center">
                  <div
                    v-tooltip.top="
                      '📞 ' +
                      getPhoneStatusLabel(
                        getAgentStatusInQueueGroup(agent, queueGroup)
                      )
                    "
                    class="status-square"
                    :class="
                      getStatusSquareClass(
                        getAgentStatusInQueueGroup(agent, queueGroup)
                      )
                    "
                  />
                  <div
                    v-if="
                      showChatStatus &&
                      getAgentChatStatusInPrefixGroup(agent, queueGroup[0])
                    "
                    v-tooltip.top="
                      '🗨 ' +
                      getChatStatusLabel(
                        getAgentChatStatusInPrefixGroup(agent, queueGroup[0])
                      )
                    "
                    class="status-square status-square-chat"
                    :class="
                      getChatStatusSquareClass(
                        getAgentChatStatusInPrefixGroup(agent, queueGroup[0])
                      )
                    "
                  />
                </div>
              </td>
            </tr>
          </tbody>
          <tfoot class="border-t border-n-weak">
            <tr class="waiting-callers-row divide-x divide-n-weak">
              <td colspan="2">&nbsp;</td>
              <td colspan="2" class="text-left font-semibold">
                {{ $t('WALLBOARD.WAITING') }}
              </td>
              <td
                v-for="queueGroup in queueGroups"
                :key="`waiting-${queueGroup[0]}`"
                class="text-center font-semibold"
              >
                {{ getWaitingCallersForQueueGroup(queueGroup) }}
              </td>
            </tr>
            <tr class="missed-calls-row divide-x divide-n-weak">
              <td colspan="2">&nbsp;</td>
              <td
                colspan="2"
                class="text-left font-semibold border-t border-n-weak"
              >
                {{ $t('WALLBOARD.MISSED_CALLS') }}
              </td>
              <td
                v-for="queueGroup in queueGroups"
                :key="`missed-${queueGroup[0]}`"
                class="text-center font-semibold border-t border-n-weak"
              >
                {{ getMissedCallsForQueueGroup(queueGroup) }}
              </td>
            </tr>
            <tr class="handled-calls-row divide-x divide-n-weak">
              <td colspan="2">&nbsp;</td>
              <td
                colspan="2"
                class="text-left font-semibold border-t border-n-weak"
              >
                {{ $t('WALLBOARD.HANDLED_CALLS') }}
              </td>
              <td
                v-for="queueGroup in queueGroups"
                :key="`handled-${queueGroup[0]}`"
                class="text-center font-semibold border-t border-n-weak"
              >
                {{ getHandledCallsForQueueGroup(queueGroup) }}
              </td>
            </tr>
            <tr class="outbound-calls-row divide-x divide-n-weak">
              <td colspan="2">&nbsp;</td>
              <td
                colspan="2"
                class="text-left font-semibold border-t border-n-weak"
              >
                {{ $t('WALLBOARD.OUTBOUND_CALLS') }}
              </td>
              <td
                v-for="queueGroup in queueGroups"
                :key="`outbound-${queueGroup[0]}`"
                class="text-center font-semibold border-t border-n-weak"
              >
                {{ getOutboundCallsForQueueGroup(queueGroup) }}
              </td>
            </tr>
            <tr class="availability-row divide-x divide-n-weak">
              <td colspan="2">&nbsp;</td>
              <td
                colspan="2"
                class="text-left font-semibold border-t border-n-weak"
              >
                <div
                  v-tooltip.top="$t('WALLBOARD.AVAILABILITY_TOOLTIP')"
                  class="flex items-center content-center gap-1"
                >
                  <span>{{ $t('WALLBOARD.AVAILABILITY') }}</span>
                  <span class="i-lucide-info size-4" />
                </div>
              </td>
              <td
                v-for="queueGroup in queueGroups"
                :key="`availability-${queueGroup[0]}`"
                class="text-center font-semibold border-t border-n-weak"
              >
                {{ getAvailabilityForQueueGroup(queueGroup) }}%
              </td>
            </tr>
          </tfoot>
        </table>
      </div>
    </div>
  </div>
</template>

<style scoped lang="scss">
.table-wrapper {
  overflow-x: auto;
  max-width: 100%;
}

.table-container {
  display: flex;
  flex-direction: column;
  align-items: start;
  overflow: hidden;
  min-width: min-content;
}

.table-container table {
  table-layout: auto;
}

.table-container td,
.table-container th {
  @apply px-4 py-1;
}

.waiting-callers-row,
.missed-calls-row,
.outbound-calls-row,
.handled-calls-row,
.availability-row {
  background-color: var(--color-background-solid-2);
  font-weight: 600;

  td:first-child {
    border-top: none !important;
    border-bottom: none !important;
  }
}

.queue-status-bubble {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  font-weight: 500;
  color: white;
  cursor: default;
  min-width: 20px;
  text-align: center;
  white-space: nowrap;
}

.dnd-badge {
  display: inline-flex;
  align-items: center;
  padding: 1px 6px;
  border-radius: 4px;
  font-size: 10px;
  font-weight: 700;
  letter-spacing: 0.03em;
  color: white;
  background-color: #f04747; /* red */
  cursor: help;
  white-space: nowrap;
}

.status-ready {
  background-color: #43b581; /* green */
}

.status-partially-ready {
  background-color: #ed7b00; /* orange */
}

.status-mixed {
  background-color: #9333ea; /* purple */
}

.status-not-ready {
  background-color: #faa61a; /* yellow */
}

.status-break {
  background-color: #d97706; /* dark orange */
}

.status-lunch {
  background-color: #fb923c; /* light orange */
}

.status-away {
  background-color: #f59e0b; /* amber (not-ready variant) */
}

.status-dnd {
  background-color: #ea580c; /* orange (not-ready variant) */
}

.status-trip {
  background-color: #ca8a04; /* yellow (not-ready variant) */
}

.status-custom-break {
  background-color: #b45309; /* amber */
}

.status-on-call {
  background-color: #007bff; /* blue */
}

.status-offline {
  background-color: #f04747; /* red */
}

.status-no-access {
  background-color: #60646c; /* grey */
}

.status-chat-online {
  background-color: #43b581; /* green — same as READY but semantically distinct */
}

.status-chat-busy {
  background-color: #faa61a; /* yellow */
}

.status-chat-offline {
  background-color: #60646c; /* grey */
}

.status-square {
  width: 20px;
  height: 20px;
  margin: 0px;

  &:last-child {
    border-top-right-radius: 3px;
    border-bottom-right-radius: 3px;
  }

  &.status-square-phone {
    border-top-left-radius: 3px;
    border-bottom-left-radius: 3px;

    &.idle {
      background-color: #43b581;
    }

    &.break {
      background-color: #faa61a;
    }

    &.on-break {
      background-color: #d97706;
    }

    &.on-lunch {
      background-color: #fb923c;
    }

    &.on-away {
      background-color: #f59e0b;
    }

    &.on-dnd {
      background-color: #ea580c;
    }

    &.on-trip {
      background-color: #ca8a04;
    }

    &.on-custom-break {
      background-color: #b45309;
    }

    &.busy {
      background-color: #007bff;
    }

    &.offline {
      background-color: #f04747;
    }

    &.no-access {
      border-width: 1px;
    }
  }

  &.status-square-chat {
    &.online {
      background-color: #43b581;
    }

    &.busy {
      background-color: #faa61a;
    }

    &.offline {
      background-color: #60646c;
    }
    &.no-access {
      border-width: 1px;
    }
  }
}

.status-square-phone.idle + .status-square-chat.no-access {
  border-color: #43b581;
}

.status-square-phone.break + .status-square-chat.no-access {
  border-color: #faa61a;
}

.status-square-phone.on-break + .status-square-chat.no-access {
  border-color: #d97706;
}

.status-square-phone.on-lunch + .status-square-chat.no-access {
  border-color: #fb923c;
}

.status-square-phone.busy + .status-square-chat.no-access {
  border-color: #007bff;
}

.status-square-phone.offline + .status-square-chat.no-access {
  border-color: #f04747;
}

.status-square-phone.no-access + .status-square-chat.no-access {
  border-color: #60646c;
  border-left: none;
}
</style>
